import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Alert,
  InputAdornment,
  IconButton,
  CircularProgress,
  Divider,
  Card,
  CardContent,
  Fade,
  Slide
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  Google as GoogleIcon,
  Email as EmailIcon,
  Lock as LockIcon,
  Login as LoginIcon,
  Build as BuildIcon
} from '@mui/icons-material';
import { loginUser, loginWithGoogle } from '../services/authService';
import { useAuth } from '../contexts/AuthContext';
import { auth } from '../config/firebase';

const LoginPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [authStatus, setAuthStatus] = useState<string | null>(null);

  const { setCurrentUser } = useAuth();
  const navigate = useNavigate();

  // Check if we're already logged in
  useEffect(() => {
    // Check if a user is already signed in
    const currentUser = auth.currentUser;
    if (currentUser) {
      console.log('User already signed in:', currentUser.uid);
      setAuthStatus(`مسجل الدخول بالفعل (${currentUser.email})`);
    } else {
      console.log('No user is signed in');
      setAuthStatus('لست مسجل الدخول');
    }
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email || !password) {
      setError('يرجى إدخال البريد الإلكتروني وكلمة المرور');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setAuthStatus('جارِ تسجيل الدخول...');

      console.log('Attempting login with email:', email);
      const user = await loginUser(email, password);
      console.log('Login successful, user data:', user);

      setCurrentUser(user);
      setAuthStatus('تم تسجيل الدخول بنجاح! جاري التحويل...');

      // Short delay before redirecting to allow user to see the success message
      setTimeout(() => {
        navigate('/admin');
      }, 1000);
    } catch (err: any) {
      console.error('Error during login:', err);
      setAuthStatus('فشل تسجيل الدخول');

      // Handle Firebase auth errors with improved messages
      switch (err.code) {
        case 'auth/user-not-found':
        case 'auth/wrong-password':
          setError('البريد الإلكتروني أو كلمة المرور غير صحيحة');
          break;
        case 'auth/too-many-requests':
          setError('تم تجاوز عدد محاولات تسجيل الدخول. يرجى المحاولة مرة أخرى لاحقاً');
          break;
        case 'auth/network-request-failed':
          setError('فشل الاتصال بالخادم. يرجى التحقق من اتصال الإنترنت لديك');
          break;
        case 'auth/invalid-email':
          setError('البريد الإلكتروني غير صالح');
          break;
        case 'auth/user-disabled':
          setError('تم تعطيل حساب المستخدم. يرجى التواصل مع المشرف');
          break;
        default:
          if (err.message === 'بيانات المستخدم غير موجودة') {
            setError('بيانات المستخدم غير موجودة في قاعدة البيانات. يرجى التواصل مع المشرف');
          } else {
            setError(`حدث خطأ أثناء تسجيل الدخول: ${err.message || 'يرجى المحاولة مرة أخرى'}`);
          }
      }
    } finally {
      setLoading(false);
    }
  };

  const handleTogglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const handleGoogleLogin = async () => {
    try {
      setLoading(true);
      setError(null);
      setAuthStatus('جارِ تسجيل الدخول باستخدام جوجل...');

      console.log('Attempting login with Google');
      const user = await loginWithGoogle();
      console.log('Google login successful, user data:', user);

      setCurrentUser(user);
      setAuthStatus('تم تسجيل الدخول بنجاح! جاري التحويل...');

      // Short delay before redirecting to allow user to see the success message
      setTimeout(() => {
        navigate('/admin');
      }, 1000);
    } catch (err: any) {
      console.error('Error during Google login:', err);
      setAuthStatus('فشل تسجيل الدخول باستخدام جوجل');

      if (err.code === 'auth/popup-closed-by-user') {
        setError('تم إغلاق نافذة تسجيل الدخول');
      } else if (err.code === 'auth/popup-blocked') {
        setError('تم حظر النافذة المنبثقة. يرجى السماح بالنوافذ المنبثقة لهذا الموقع');
      } else if (err.code === 'auth/account-exists-with-different-credential') {
        setError('هناك حساب موجود بالفعل بنفس البريد الإلكتروني ولكن بطريقة تسجيل دخول مختلفة');
      } else {
        setError(`حدث خطأ أثناء تسجيل الدخول: ${err.message || 'يرجى المحاولة مرة أخرى'}`);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'radial-gradient(circle at 20% 80%, rgba(120, 119, 198, 0.3) 0%, transparent 50%), radial-gradient(circle at 80% 20%, rgba(255, 255, 255, 0.1) 0%, transparent 50%)',
          pointerEvents: 'none'
        }
      }}
    >
      <Container maxWidth="sm" sx={{ position: 'relative', zIndex: 1 }}>
        <Fade in timeout={1000}>
          <Card
            elevation={24}
            sx={{
              borderRadius: 4,
              background: 'linear-gradient(145deg, rgba(255,255,255,0.95) 0%, rgba(255,255,255,0.9) 100%)',
              backdropFilter: 'blur(20px)',
              border: '1px solid rgba(255,255,255,0.2)',
              boxShadow: '0 20px 60px rgba(0,0,0,0.1)',
              overflow: 'hidden'
            }}
          >
            <CardContent sx={{ p: 0 }}>
              {/* Header Section */}
              <Box
                sx={{
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  color: 'white',
                  p: 4,
                  textAlign: 'center',
                  position: 'relative',
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    height: '20px',
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    clipPath: 'polygon(0 0, 100% 0, 100% 100%, 0 80%)'
                  }
                }}
              >
                <Slide direction="down" in timeout={800}>
                  <Box>
                    <BuildIcon sx={{ fontSize: 60, mb: 2, opacity: 0.9 }} />
                    <Typography
                      variant="h4"
                      component="h1"
                      gutterBottom
                      sx={{
                        fontWeight: 'bold',
                        textShadow: '0 2px 4px rgba(0,0,0,0.1)'
                      }}
                    >
                      نظام إدارة مركز الصيانة
                    </Typography>
                    <Typography
                      variant="h6"
                      sx={{
                        opacity: 0.9,
                        fontWeight: 300
                      }}
                    >
                      🔐 تسجيل الدخول
                    </Typography>
                  </Box>
                </Slide>
              </Box>

              {/* Form Section */}
              <Box sx={{ p: 4, pt: 5 }}>
                <Slide direction="up" in timeout={1200}>
                  <Box>
                    {authStatus && (
                      <Alert
                        severity="info"
                        sx={{
                          mb: 3,
                          borderRadius: 2,
                          background: 'linear-gradient(135deg, rgba(33, 150, 243, 0.1) 0%, rgba(33, 150, 243, 0.05) 100%)',
                          border: '1px solid rgba(33, 150, 243, 0.2)'
                        }}
                      >
                        {authStatus}
                      </Alert>
                    )}

                    {error && (
                      <Alert
                        severity="error"
                        sx={{
                          mb: 3,
                          borderRadius: 2,
                          background: 'linear-gradient(135deg, rgba(244, 67, 54, 0.1) 0%, rgba(244, 67, 54, 0.05) 100%)',
                          border: '1px solid rgba(244, 67, 54, 0.2)'
                        }}
                      >
                        {error}
                      </Alert>
                    )}

                    <Box component="form" onSubmit={handleSubmit}>
                      <TextField
                        label="البريد الإلكتروني"
                        type="email"
                        variant="outlined"
                        fullWidth
                        required
                        margin="normal"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        autoComplete="email"
                        disabled={loading}
                        sx={{
                          mb: 3,
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 3,
                            background: 'linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%)',
                            '&:hover': {
                              boxShadow: '0 4px 20px rgba(102, 126, 234, 0.15)'
                            },
                            '&.Mui-focused': {
                              boxShadow: '0 4px 20px rgba(102, 126, 234, 0.25)'
                            }
                          }
                        }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <EmailIcon sx={{ color: '#667eea' }} />
                            </InputAdornment>
                          )
                        }}
                      />

                      <TextField
                        label="كلمة المرور"
                        type={showPassword ? 'text' : 'password'}
                        variant="outlined"
                        fullWidth
                        required
                        margin="normal"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        autoComplete="current-password"
                        disabled={loading}
                        sx={{
                          mb: 3,
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 3,
                            background: 'linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%)',
                            '&:hover': {
                              boxShadow: '0 4px 20px rgba(102, 126, 234, 0.15)'
                            },
                            '&.Mui-focused': {
                              boxShadow: '0 4px 20px rgba(102, 126, 234, 0.25)'
                            }
                          }
                        }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <LockIcon sx={{ color: '#667eea' }} />
                            </InputAdornment>
                          ),
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                onClick={handleTogglePasswordVisibility}
                                edge="end"
                                disabled={loading}
                                sx={{ color: '#667eea' }}
                              >
                                {showPassword ? <VisibilityOff /> : <Visibility />}
                              </IconButton>
                            </InputAdornment>
                          )
                        }}
                      />

                      <Button
                        type="submit"
                        variant="contained"
                        fullWidth
                        size="large"
                        disabled={loading}
                        sx={{
                          mt: 2,
                          mb: 3,
                          py: 2,
                          borderRadius: 3,
                          background: 'linear-gradient(45deg, #667eea 30%, #764ba2 90%)',
                          fontSize: '1.1rem',
                          fontWeight: 'bold',
                          boxShadow: '0 8px 25px rgba(102, 126, 234, 0.3)',
                          '&:hover': {
                            background: 'linear-gradient(45deg, #764ba2 30%, #667eea 90%)',
                            transform: 'translateY(-2px)',
                            boxShadow: '0 12px 35px rgba(102, 126, 234, 0.4)'
                          },
                          '&:disabled': {
                            background: 'linear-gradient(45deg, #e0e0e0 30%, #bdbdbd 90%)',
                            color: '#9e9e9e'
                          }
                        }}
                        startIcon={loading ? null : <LoginIcon />}
                      >
                        {loading ? (
                          <CircularProgress size={24} color="inherit" />
                        ) : (
                          '🔐 تسجيل الدخول'
                        )}
                      </Button>

                      <Divider sx={{ my: 3, '&::before, &::after': { borderColor: 'rgba(102, 126, 234, 0.2)' } }}>
                        <Typography variant="body2" color="text.secondary">
                          أو
                        </Typography>
                      </Divider>

                      <Button
                        variant="outlined"
                        fullWidth
                        size="large"
                        disabled={loading}
                        onClick={handleGoogleLogin}
                        startIcon={<GoogleIcon />}
                        sx={{
                          mb: 3,
                          py: 1.5,
                          borderRadius: 3,
                          border: '2px solid #db4437',
                          color: '#db4437',
                          fontWeight: 'bold',
                          '&:hover': {
                            background: 'linear-gradient(45deg, #db4437 0%, #c23321 100%)',
                            color: 'white',
                            transform: 'translateY(-2px)',
                            boxShadow: '0 8px 25px rgba(219, 68, 55, 0.3)'
                          }
                        }}
                      >
                        🌐 تسجيل الدخول باستخدام جوجل
                      </Button>

                      <Button
                        variant="text"
                        fullWidth
                        disabled={loading}
                        onClick={() => navigate('/register')}
                        sx={{
                          py: 1.5,
                          borderRadius: 3,
                          color: '#667eea',
                          fontWeight: 'bold',
                          '&:hover': {
                            background: 'linear-gradient(45deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)',
                            transform: 'translateY(-1px)'
                          }
                        }}
                      >
                        ✨ إنشاء حساب جديد
                      </Button>
                    </Box>
                  </Box>
                </Slide>
              </Box>
            </CardContent>
          </Card>
        </Fade>
      </Container>
    </Box>
  );
};

export default LoginPage;